import { formatName } from "../../../../utils/format-name.ts";
import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";

import { Button, FileField, TextField } from "../../../ui/index.ts";
import { bankStepSchema, type BankStepValues } from "../schemas/onboard-post-join.schema.ts";
import { validatePhase2DocumentFile } from "../utils/validate-phase2-document-file.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";

import styles from "./bank-step.module.css";

export type BankStepProps = {
  defaultValues: BankStepValues;
  cancelledCheque: File | null;
  onCancelledChequeChange: (file: File | null) => void;
  chequeError?: string;
  loading: boolean;
  onBack?: () => void;
  onNext: (values: BankStepValues, cheque: File) => void | Promise<void>;
  formId?: string;
  hideFooter?: boolean;
  onResetReady?: (reset: () => void) => void;
};

const field = (name: keyof BankStepValues) => name;

const EMPTY_BANK: BankStepValues = {
  beneficiaryName: "",
  accountNumber: "",
  bankName: "",
  branchName: "",
  ifscCode: "",
};

export function BankStep({
  defaultValues,
  cancelledCheque,
  onCancelledChequeChange,
  chequeError,
  loading,
  onBack,
  onNext,
  formId,
  hideFooter = false,
  onResetReady,
}: BankStepProps) {
  const [missingCheque, setMissingCheque] = useState(false);
  const [chequeSizeError, setChequeSizeError] = useState<string | undefined>();
  const [chequeFieldKey, setChequeFieldKey] = useState(0);
  const form = useForm({
    defaultValues,
    validators: { onSubmit: bankStepSchema },
    onSubmit: async ({ value }) => {
      if (!cancelledCheque) {
        setMissingCheque(true);
        return;
      }
      const sizeErr = validatePhase2DocumentFile(cancelledCheque);
      if (sizeErr) {
        setChequeSizeError(sizeErr);
        return;
      }
      setMissingCheque(false);
      setChequeSizeError(undefined);
      await onNext(value, cancelledCheque);
    },
  });

  const chequeFieldError =
    chequeError ??
    chequeSizeError ??
    (missingCheque ? "Please attach a cancelled cheque." : undefined);

  useEffect(() => {
    onResetReady?.(() => {
      form.reset(EMPTY_BANK);
      onCancelledChequeChange(null);
      setMissingCheque(false);
      setChequeSizeError(undefined);
      setChequeFieldKey((k) => k + 1);
    });
  }, [form, onCancelledChequeChange, onResetReady]);

  const handleChequeChange = (file: File | null) => {
    setChequeSizeError(undefined);
    if (!file) {
      onCancelledChequeChange(null);
      return;
    }
    const err = validatePhase2DocumentFile(file);
    if (err) {
      setChequeSizeError(err);
      onCancelledChequeChange(null);
      setChequeFieldKey((k) => k + 1);
      return;
    }
    onCancelledChequeChange(file);
    setMissingCheque(false);
  };

  return (
    <form
      id={formId}
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.grid}>
        <form.Field name={field("beneficiaryName")}>
          {(f) => (
            <TextField
              id="bank-beneficiary"
              label="Beneficiary Name"
              maxLength={140}
              value={f.state.value}
              onChange={(e) => f.handleChange(formatName(e.target.value))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("accountNumber")}>
          {(f) => (
            <TextField
              id="bank-account"
              label="Account Number"
              maxLength={18}
              value={f.state.value}
              onChange={(e) => {
                const value = e.target.value

                const sanitzedValue = value.replace(/[^0-9]/g, '');
                f.handleChange(sanitzedValue)
              }}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("bankName")}>
          {(f) => (
            <TextField
              id="bank-name"
              label="Bank Name"
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value)}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("branchName")}>
          {(f) => (
            <TextField
              id="bank-branch"
              label="Branch Name"
              value={f.state.value}
              onChange={(e) => f.handleChange(formatName(e.target.value))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("ifscCode")}>
          {(f) => (
            <TextField
              id="bank-ifsc"
              label="IFSC Code"
              maxLength={11}
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value)}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
      </div>
      <FileField
        key={`bank-cheque-${chequeFieldKey}`}
        id="bank-cheque"
        label="Cancelled Cheque (scan or photo)"
        required
        accept="image/*,.pdf,application/pdf"
        stagedFile={cancelledCheque}
        onChange={handleChequeChange}
        error={chequeFieldError}
        disabled={loading}
      />
      {hideFooter ? null : (
        <div className={styles.actions}>
          {onBack ? (
            <Button type="button" variant="muted-outline" disabled={loading} onClick={onBack}>
              Back
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={loading}>
            {loading ? "Saving…" : "Next"}
          </Button>
        </div>
      )}
    </form>
  );
}
